import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  ChevronLeft,
  KeyRound,
  LogOut,
  Menu,
  Monitor,
  Moon,
  MonitorSmartphone,
  PanelLeft,
  Sun,
  UserRound,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogTitle, SheetContent } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  TooltipProvider,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, initialsOf } from '@/components/ui/misc';
import { ErrorBoundary } from '@/components/feedback/ErrorBoundary';
import { OfflineBanner } from '@/components/states';
import { InstallPrompt, UpdatePrompt } from '@/pwa/PwaPrompts';
import { InstallAppButton } from '@/pwa/InstallAppButton';
import { Breadcrumbs } from './Breadcrumbs';
import { CommandPalette } from './CommandPalette';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuthActions } from '@/features/auth/useAuth';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { useBrowserNotifications } from '@/features/notifications/useBrowserNotifications';
import { mobileItems, sidebarFooterItems, visibleSections } from '@/lib/navigation';
import { getStoredTheme, setTheme, type ThemePreference } from '@/lib/theme';
import { firstNameOf, greeting } from '@/lib/greeting';
import { paths } from '@/routes/paths';
import { cn } from '@/lib/cn';

/**
 * The authenticated application shell.
 *
 * Layout: the chrome (topbar, sidebar) sits directly on `canvas`, and the content is an
 * inset `surface` panel with a hairline border. That single move does most of the work
 * of making the product feel considered — it separates navigation from content by
 * PLANE rather than by a divider line, which is why the active nav item can read as a
 * chip lifted onto the content plane.
 *
 * Navigation items are filtered by PERMISSION KEY AND SCOPE — forbidden items are
 * absent, not disabled. A disabled control advertises a capability the user lacks.
 */

function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}

function NavItems({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const { can, role } = usePermissions();
  const sections = visibleSections(can, role);
  const footer = sidebarFooterItems(can, role);

  return (
    <nav aria-label="Main" className="flex-1 space-y-5 overflow-y-auto px-2 py-1">
      {[
        ...sections,
        ...(footer.length > 0 ? [{ id: 'general', label: 'General', items: footer }] : []),
      ].map((section) => (
        <div key={section.id}>
          {!collapsed && (
            <h2 className="px-3 pb-1.5 text-caption font-semibold uppercase tracking-wider text-fg-muted">
              {section.label}
            </h2>
          )}
          <ul className="space-y-0.5">
            {section.items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  onClick={onNavigate}
                  title={collapsed ? item.label : undefined}
                  className={({ isActive }) =>
                    cn(
                      'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-body',
                      'transition-colors duration-150',
                      collapsed && 'justify-center px-0',
                      isActive
                        ? // A filled brand pill. White on --color-action measures 5.48:1.
                          'bg-action font-medium text-on-action shadow-sm'
                        : 'text-fg-muted hover:bg-surface/70 hover:text-fg',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <item.icon
                        aria-hidden
                        className={cn(
                          'size-4 shrink-0 transition-colors',
                          // currentColor on the active row: a fixed link colour would
                          // vanish against the filled green behind it.
                          isActive ? 'text-current' : 'text-fg-muted group-hover:text-fg',
                        )}
                      />
                      {!collapsed && <span className="truncate">{item.label}</span>}
                      {collapsed && <span className="sr-only">{item.label}</span>}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

/**
 * The Bemedic Academy mark, and the product's name beside it.
 *
 * The mark sits on a WHITE tile in both themes, deliberately. It is dark-grey and green
 * artwork, and on the dark panel the grey figures measure around 1.3:1 — the logo would
 * be a green squiggle with half of it missing. A fixed light tile is how a multicolour
 * mark is normally carried into a dark interface, and it keeps the brand's own colours
 * rather than recolouring someone else's logo.
 *
 * `alt=""` because the product name sits next to it in text: a screen reader announcing
 * "Bemedic Academy logo, BM-Work" says the same thing twice.
 */
function Logo({ compact = false }: { compact?: boolean }) {
  return (
    <span className="flex items-center gap-2">
      <span className="grid size-7 shrink-0 place-items-center rounded-md bg-white shadow-xs">
        <img src="/icons/logo-mark.png" alt="" width={20} height={20} className="size-5" />
      </span>
      {!compact && <span className="font-semibold tracking-tight">BM-Work</span>}
    </span>
  );
}

function ThemeMenu() {
  const [pref, setPref] = useState<ThemePreference>(getStoredTheme);
  const Icon = pref === 'dark' ? Moon : pref === 'light' ? Sun : Monitor;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <Icon aria-hidden className="size-4" />
          <span className="sr-only">Colour theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Theme</DropdownMenuLabel>
        {(['light', 'dark', 'system'] as const).map((value) => (
          <DropdownMenuCheckboxItem
            key={value}
            checked={pref === value}
            onCheckedChange={() => {
              setTheme(value);
              setPref(value);
            }}
          >
            {value[0]!.toUpperCase() + value.slice(1)}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** `SUPER_ADMIN` → `Super admin`. The enum is a key, not something to show as-is. */
function roleLabel(role: string): string {
  const words = role.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The signed-in user: who they are, and the three things they can do about it. */
function ProfileMenu() {
  const { user, role } = usePermissions();
  const { logout } = useAuthActions();

  // The display name is the employee record's; an account without an employee record
  // (a bootstrap super admin) falls back to the email rather than rendering blank.
  const name = user?.employee?.fullName ?? user?.email ?? '';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2 pl-1 pr-2">
          <Avatar className="size-6">
            <AvatarFallback>{initialsOf(name)}</AvatarFallback>
          </Avatar>
          <span className="hidden max-w-[12ch] truncate text-caption font-medium sm:inline">
            {user?.employee?.fullName ?? user?.email}
          </span>
          <span className="sr-only">Your account</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>
          <span className="block truncate">{name}</span>
          <span className="mt-0.5 block truncate font-normal text-fg-muted">{user?.email}</span>
          {role && <span className="mt-1 block font-normal text-fg-muted">{roleLabel(role)}</span>}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <Link to={paths.me}>
            <UserRound aria-hidden className="size-4" />
            My profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={paths.sessions}>
            <MonitorSmartphone aria-hidden className="size-4" />
            Sessions and devices
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={paths.changePassword}>
            <KeyRound aria-hidden className="size-4" />
            Change password
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void logout()}>
          <LogOut aria-hidden className="size-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AppShell() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('bemedic.sidebarCollapsed') === 'true';
    } catch {
      return false;
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { can, role, user } = usePermissions();
  const firstName = firstNameOf(user);
  const online = useOnlineStatus();
  const location = useLocation();

  // One connection for the whole application, opened here because the shell is the one
  // component that is mounted for exactly as long as the user is signed in. Opening it
  // per page would reconnect on every navigation.
  useBrowserNotifications(online);

  useEffect(() => {
    try {
      localStorage.setItem('bemedic.sidebarCollapsed', String(collapsed));
    } catch {
      /* cosmetic only */
    }
  }, [collapsed]);

  /**
   * Move focus to the page heading on navigation.
   *
   * Without it, keyboard and screen-reader users are returned to the top of the
   * navigation after every click. Headings carry `tabIndex={-1}` so they can receive
   * focus without joining the tab order.
   *
   * It WAITS for the heading rather than looking once, because route groups are lazily
   * loaded: on the first navigation into a module the Suspense fallback is on screen
   * and there is no heading yet, so a single attempt focuses nothing and the user lands
   * nowhere. A MutationObserver catches it whenever the chunk arrives, and a timeout
   * stops it waiting forever on a route that renders no heading at all.
   */
  const { pathname } = location;
  useEffect(() => {
    if (!pathname) return;

    const focusHeading = (): boolean => {
      const heading = document.querySelector<HTMLElement>('main h1');
      if (!heading) return false;
      heading.focus();
      return true;
    };

    if (focusHeading()) return;

    const observer = new MutationObserver(() => {
      if (focusHeading()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Bounded: a slow chunk is worth waiting for, a missing heading is not.
    const timeout = setTimeout(() => observer.disconnect(), 3_000);

    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, [pathname]);

  const bottomItems = mobileItems(can, role);

  return (
    <TooltipProvider delayDuration={300}>
      <div className="min-h-screen bg-canvas text-fg">
        <a
          href="#main"
          className="sr-only-focusable absolute left-4 top-3 z-50 rounded-md bg-action px-3 py-2 text-caption text-on-action shadow-action"
        >
          Skip to content
        </a>

        {/* Chrome sits on the canvas — no border, the content panel provides the edge.
            The top safe-area inset matters once this is installed: in standalone mode
            the header runs under the status bar without it. */}
        <header className="sticky top-0 z-40 bg-canvas/85 pt-[env(safe-area-inset-top)] backdrop-blur-md">
          <div className="flex h-14 items-center gap-2 px-3 sm:px-4">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setDrawerOpen(true)}
            >
              <Menu aria-hidden className="size-5" />
              <span className="sr-only">Open navigation</span>
            </Button>

            {/* Mobile opens on a greeting rather than a logo. An installed app already
                shows its name on the home screen and in the task switcher; repeating it
                here spends the only wide row on the screen saying nothing. */}
            <div className="min-w-0 flex-1 md:hidden">
              <p className="truncate text-caption leading-tight text-fg-muted">{greeting()}</p>
              <p className="truncate text-body font-semibold leading-tight">{firstName}</p>
            </div>

            <div
              className="hidden w-60 items-center gap-1 md:flex"
              style={collapsed ? { width: '4rem' } : undefined}
            >
              {!collapsed && <Logo />}
              <Button
                variant="ghost"
                size="icon"
                className={cn('ml-auto', collapsed && 'mx-auto')}
                onClick={() => setCollapsed((v) => !v)}
              >
                <PanelLeft aria-hidden className="size-4" />
                <span className="sr-only">{collapsed ? 'Expand sidebar' : 'Collapse sidebar'}</span>
              </Button>
            </div>

            {/* The search pill takes the free width on desktop; the same control is a
                circular icon button on mobile. One instance, so one dialog. */}
            <div className="flex min-w-0 flex-1 justify-end md:justify-start">
              <CommandPalette />
            </div>

            <div className="flex items-center gap-1">
              {/* Hidden on mobile: InstallPrompt already offers this as a banner, and
                  the theme menu moves into the drawer, which has room for it. */}
              <div className="hidden md:contents">
                <InstallAppButton />
              </div>
              <NotificationBell />
              <div className="hidden md:contents">
                <ThemeMenu />
              </div>
              <div className="mx-1 hidden h-5 w-px bg-line-strong md:block" />
              <ProfileMenu />
            </div>
          </div>
        </header>

        {!online && <OfflineBanner />}

        {/* Both self-hide; neither takes focus on appearing. */}
        <UpdatePrompt />
        <InstallPrompt />

        <div className="flex gap-0 px-0 md:pl-3">
          <aside
            className={cn(
              'sticky top-14 hidden h-[calc(100vh-3.5rem)] shrink-0 flex-col pb-3 md:flex',
              collapsed ? 'w-16' : 'w-60',
            )}
          >
            <NavItems collapsed={collapsed} />
          </aside>

          {/* The inset content panel. */}
          <div className="min-w-0 flex-1 md:p-3 md:pl-2 md:pt-0">
            <div
              className={cn(
                'min-h-[calc(100vh-4.5rem)] bg-surface',
                'md:rounded-xl md:border md:border-line md:shadow-sm',
              )}
            >
              <div className="border-b border-line px-4 py-2 md:px-5 md:py-2.5">
                <Breadcrumbs />
              </div>

              {/* Tighter gutters on a phone so cards run closer to the edge, the way a
                  native list does. pb-24 clears the 64px bottom bar and its inset. */}
              <main id="main" className="mx-auto max-w-[1400px] px-4 py-5 pb-24 md:px-5 md:py-6 md:pb-8">
                {/* Keyed on the path: a crash on one page must not follow the user
                    to the next one. See ErrorBoundary's docblock. */}
                <ErrorBoundary area="this page" resetKey={pathname}>
                  <Outlet />
                </ErrorBoundary>
              </main>
            </div>
          </div>
        </div>

        {/* Mobile drawer */}
        <Dialog open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetContent side="left" className="bg-canvas">
            <DialogTitle className="sr-only">Navigation</DialogTitle>
            <div className="flex h-14 items-center justify-between px-3">
              <Logo />
              <Button variant="ghost" size="icon" onClick={() => setDrawerOpen(false)}>
                <ChevronLeft aria-hidden className="size-4" />
                <span className="sr-only">Close navigation</span>
              </Button>
            </div>
            <NavItems collapsed={false} onNavigate={() => setDrawerOpen(false)} />

            {/* The theme control lives here on mobile: the header has no room for it
                once the greeting takes the wide row. */}
            <div className="mt-auto flex items-center justify-between border-t border-line px-4 py-3">
              <span className="text-caption text-fg-muted">Appearance</span>
              <ThemeMenu />
            </div>
          </SheetContent>
        </Dialog>

        {/* Mobile bottom bar — the five most-used destinations, two taps from anywhere.
            The active state is a filled pill behind the icon, not just a colour change:
            at this size a hue shift alone is easy to miss, and it would be the only
            signal. Each tab is 64px tall, comfortably over the 44px target minimum. */}
        <nav
          aria-label="Primary"
          className={cn(
            'fixed inset-x-0 bottom-0 z-40 grid border-t border-line bg-surface/95 backdrop-blur-md md:hidden',
            'pb-[env(safe-area-inset-bottom)]',
          )}
          style={{ gridTemplateColumns: `repeat(${bottomItems.length}, minmax(0, 1fr))` }}
        >
          {bottomItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex h-16 flex-col items-center justify-center gap-1 text-caption font-medium',
                  'transition-colors duration-150',
                  isActive ? 'text-link' : 'text-fg-muted',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={cn(
                      'grid h-7 w-12 place-items-center rounded-full transition-colors duration-150',
                      isActive && 'bg-selected',
                    )}
                  >
                    <item.icon aria-hidden className="size-5" />
                  </span>
                  <span className="truncate px-1 leading-none">{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </div>
    </TooltipProvider>
  );
}
